import React from 'react';
import './MobilePrivacyPage.css';

// Full Privacy Policy rendered in mobile style (no link to separate desktop page)
export default function MobilePrivacyPage(){
  const scrollToTop = () => window.scrollTo({ top:0, behavior:'smooth' });

  return (
    <div className="mobile-privacy-page">
      <header className="mpp-header" role="banner">
        <a href="/m" className="mpp-home-btn" aria-label="Go home">
          <svg className="mpp-home-ic" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
            <defs>
              <linearGradient id="mppHomeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6b7280"/>
                <stop offset="55%" stopColor="#9ca3af"/>
                <stop offset="100%" stopColor="#d1d5db"/>
              </linearGradient>
            </defs>
            <path d="M4 11.5 12 5l8 6.5v6.2c0 .9-.7 1.6-1.6 1.6H5.6c-.9 0-1.6-.7-1.6-1.6v-6.2Z" fill="url(#mppHomeGrad)" stroke="rgba(15,32,58,0.15)" strokeWidth="1" strokeLinejoin="round"/>
            <path d="M9.75 19.3v-4.1c0-.7.55-1.2 1.25-1.2h2c.7 0 1.25.5 1.25 1.2v4.1" fill="none" stroke="#fff" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" opacity="0.85"/>
          </svg>
        </a>
        <h1 className="mpp-title">Privacy Policy</h1>
        <p className="mpp-sub">At DocuHounds, we believe your privacy is fundamental. This policy explains how we collect, use, and protect your information when you use our AI-powered academic assistant.</p>
        <div className="mpp-accent" />
      </header>

      <nav className="mpp-toc" aria-label="Table of contents">
        <ul>
          <li><a href="#introduction">1. Introduction</a></li>
          <li><a href="#information-collected">2. Information We Collect</a></li>
          <li><a href="#how-we-use">3. How We Use Your Information</a></li>
          <li><a href="#data-security">4. Data Storage & Security</a></li>
          <li><a href="#third-party">5. Third-Party Services</a></li>
          <li><a href="#student-protection">6. Student & Minor Protection</a></li>
          <li><a href="#your-rights">7. Your Rights</a></li>
          <li><a href="#cookies">8. Cookies</a></li>
          <li><a href="#policy-changes">9. Changes to This Policy</a></li>
          <li><a href="#contact">10. Contact Us</a></li>
        </ul>
      </nav>

      {/* Sections adapted from desktop policy with mobile classes */}
      <section id="introduction" className="mpp-section">
        <h2 className="mpp-section-title">1. Introduction</h2>
        <p className="mpp-text">DocuHounds is an AI-powered academic assistant designed to help students organize coursework, track assignments, and better manage their academic workflow. We provide tools for syllabus upload, calendar synchronization, task management, and AI-powered document interaction.</p>
        <div className="mpp-subcard">
          <h3 className="mpp-subcard-title">🛡️ Our Commitment to Student Privacy</h3>
          <p className="mpp-text">We are committed to protecting student privacy and maintaining the highest standards of data security. Your academic information is never sold, shared inappropriately, or used for advertising purposes.</p>
        </div>
        <p className="mpp-text">This Privacy Policy applies to all users of DocuHounds, including students, educators, and parents. By using our service, you agree to the collection and use of information in accordance with this policy.</p>
      </section>

      <section id="information-collected" className="mpp-section">
        <h2 className="mpp-section-title">2. Information We Collect</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📝 Personal Information</h3>
          <ul className="mpp-list">
            <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
            <li><strong>Profile Details:</strong> School affiliation (optional), grade level, academic interests</li>
            <li><strong>Communication:</strong> Messages you send us for support or feedback</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📚 Academic Files & Content</h3>
          <ul className="mpp-list">
            <li><strong>Uploaded Documents:</strong> Syllabi, assignments, notes, and other academic materials</li>
            <li><strong>Calendar Data:</strong> Assignment due dates, exam schedules, class times (when synced)</li>
            <li><strong>AI Interactions:</strong> Questions you ask our AI assistant and related conversations</li>
            <li><strong>Task Data:</strong> To-do items, priorities, and progress tracking</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📊 Usage Data</h3>
          <ul className="mpp-list">
            <li><strong>App Usage:</strong> Features accessed, time spent, interaction patterns</li>
            <li><strong>Performance Data:</strong> Load times, error reports, system performance metrics</li>
            <li><strong>Device Information:</strong> Browser type, operating system, screen resolution</li>
            <li><strong>Network Information:</strong> IP address, general location (city/state level)</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>Important:</strong> We only collect information necessary to provide and improve our services. You can use DocuHounds with minimal personal information.</div>
      </section>

      <section id="how-we-use" className="mpp-section">
        <h2 className="mpp-section-title">3. How We Use Your Information</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🎯 Core Service Delivery</h3>
          <ul className="mpp-list">
            <li>Parse and organize your uploaded syllabi and documents</li>
            <li>Sync assignment deadlines with your external calendars</li>
            <li>Provide AI-powered answers about your course materials</li>
            <li>Generate personalized task dashboards and study schedules</li>
            <li>Send reminders and notifications about upcoming deadlines</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">✨ Personalization & Improvement</h3>
          <ul className="mpp-list">
            <li>Customize your dashboard based on your academic preferences</li>
            <li>Improve AI responses through machine learning (anonymized)</li>
            <li>Develop new features based on user feedback and usage patterns</li>
            <li>Optimize app performance and fix technical issues</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🤝 Support & Communication</h3>
          <ul className="mpp-list">
            <li>Respond to your support requests and technical questions</li>
            <li>Send important updates about service changes or new features</li>
            <li>Provide educational content and study tips (opt-in only)</li>
            <li>Conduct user research to better understand student needs</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📈 Analytics & Research</h3>
          <ul className="mpp-list">
            <li>Analyze anonymized usage patterns to improve our AI algorithms</li>
            <li>Generate aggregated insights about student study habits (no personal data)</li>
            <li>Monitor system performance and security threats</li>
            <li>Comply with legal requirements and prevent misuse</li>
          </ul>
        </div>
        <div className="mpp-danger-box">
          <h3 className="mpp-subcard-title">🚫 What We DON'T Do</h3>
          <ul className="mpp-list">
            <li>We never sell your personal information to third parties</li>
            <li>We don't use your data for advertising or marketing to you</li>
            <li>We don't share your academic content with other users or institutions</li>
            <li>We don't track you across other websites or apps</li>
          </ul>
        </div>
      </section>

      <section id="data-security" className="mpp-section">
        <h2 className="mpp-section-title">4. Data Storage & Security</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🔐 Encryption & Protection</h3>
          <ul className="mpp-list">
            <li><strong>Data in Transit:</strong> All data is encrypted using TLS 1.3 during transmission</li>
            <li><strong>Data at Rest:</strong> Files and database records are encrypted using AES-256</li>
            <li><strong>Password Security:</strong> User passwords are hashed and salted using bcrypt</li>
            <li><strong>Access Controls:</strong> Multi-factor authentication for all staff accounts</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🏢 Infrastructure & Compliance</h3>
          <ul className="mpp-list">
            <li><strong>SOC 2 Compliant:</strong> Infrastructure meets Type II SOC 2 standards</li>
            <li><strong>Cloud Security:</strong> Data hosted on AWS with enterprise-grade controls</li>
            <li><strong>Regular Audits:</strong> Third-party assessments and penetration testing</li>
            <li><strong>Backup Systems:</strong> Encrypted, geographically distributed backups</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">👥 Access Management</h3>
          <ul className="mpp-list">
            <li><strong>Least Privilege:</strong> Staff access limited to job requirements</li>
            <li><strong>Access Logging:</strong> All access is monitored and logged</li>
            <li><strong>Training:</strong> Regular security awareness for team members</li>
            <li><strong>Background Checks:</strong> Clearance for personnel with elevated access</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>Data Retention:</strong> Academic files retained for account duration plus 30 days for recovery unless deleted earlier.</div>
      </section>

      <section id="third-party" className="mpp-section">
        <h2 className="mpp-section-title">5. Third-Party Services</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📅 Calendar Integrations</h3>
          <ul className="mpp-list">
            <li>Only access data necessary for scheduling</li>
            <li>Create new events; do not read unrelated personal events</li>
            <li>Revocable access through account settings</li>
            <li>Providers have independent privacy policies</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">💳 Payment Processing</h3>
          <ul className="mpp-list">
            <li><strong>Stripe / PayPal:</strong> Securely handle transactions</li>
            <li>No storage of full payment details on our servers</li>
            <li>Subject to PCI DSS compliance</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📊 Analytics & Monitoring</h3>
          <ul className="mpp-list">
            <li>Error tracking & performance monitoring (anonymized)</li>
            <li>Aggregated usage statistics only</li>
            <li>No advertising networks or tracking pixels</li>
            <li>Data anonymized and non-identifiable</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>External Links:</strong> We may link to external sites; review their policies before sharing information.</div>
      </section>

      <section id="student-protection" className="mpp-section">
        <h2 className="mpp-section-title">6. Student & Minor Protection</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📋 FERPA Compliance</h3>
          <ul className="mpp-list">
            <li>Educational records protected with safeguards</li>
            <li>Data used only for legitimate educational purposes</li>
            <li>Detailed logging of access and sharing</li>
            <li>Access rights for parents and students</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">👶 Age Requirements & Consent</h3>
          <ul className="mpp-list">
            <li><strong>13+:</strong> Platform designed for students aged 13 and older</li>
            <li><strong>Parental Approval:</strong> Recommended for users under 18</li>
            <li><strong>School Authorization:</strong> Institutional consent processes supported</li>
            <li><strong>COPPA:</strong> No data knowingly collected from children under 13</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🏫 Institution Support</h3>
          <ul className="mpp-list">
            <li>Schools can request deletion anytime</li>
            <li>Data processing agreements available</li>
            <li>No commercial use beyond service delivery</li>
            <li>Bulk account management features</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>Parents:</strong> If a child under 13 creates an account, contact <a href="mailto:privacy@docuhounds.com">privacy@docuhounds.com</a> for prompt deletion.</div>
      </section>

      <section id="your-rights" className="mpp-section">
        <h2 className="mpp-section-title">7. Your Rights</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🗑️ Data Deletion Rights</h3>
          <ul className="mpp-list">
            <li>Delete entire account and associated data</li>
            <li>Remove specific documents or materials</li>
            <li>Right to be forgotten within 30 days</li>
            <li>Confirmation upon completion</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📤 Data Export Rights</h3>
          <ul className="mpp-list">
            <li>Export documents in common formats</li>
            <li>Receive copy of profile & account data</li>
            <li>Export AI chat history</li>
            <li>Machine-readable formats where possible</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">✉️ Communication Preferences</h3>
          <ul className="mpp-list">
            <li>Opt-out of non-essential emails</li>
            <li>Choose channels & frequency</li>
            <li>Opt out of promotional content</li>
            <li>Customize in-app notifications</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🔍 Access & Correction Rights</h3>
          <ul className="mpp-list">
            <li>Request details of stored personal data</li>
            <li>Correct inaccurate information</li>
            <li>Understand processing purposes</li>
            <li>Port data to another provider</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>Exercising Rights:</strong> Visit Account Settings or email <a href="mailto:privacy@docuhounds.com">privacy@docuhounds.com</a>. Responses within 5 business days; most requests complete within 30.</div>
      </section>

      <section id="cookies" className="mpp-section">
        <h2 className="mpp-section-title">8. Cookies</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🍪 Essential Cookies</h3>
          <ul className="mpp-list">
            <li>Authentication session management</li>
            <li>Security protections (CSRF)</li>
            <li>Preferences retention</li>
            <li>Load balancing & performance</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📊 Analytics Cookies (Optional)</h3>
          <ul className="mpp-list">
            <li>Usage patterns to improve features</li>
            <li>Performance metrics (load times, errors)</li>
            <li>Feature adoption tracking</li>
            <li>Anonymized & aggregated only</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">⚙️ Managing Cookies</h3>
          <ul className="mpp-list">
            <li>Disable via browser settings</li>
            <li>Block analytics while keeping essential</li>
            <li>Consent banner preference control</li>
            <li>Adjust analytics in Account Settings</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>No Tracking:</strong> No advertising cookies, social pixels, or cross-site profiling.</div>
      </section>

      <section id="policy-changes" className="mpp-section">
        <h2 className="mpp-section-title">9. Changes to This Policy</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📢 How We Handle Updates</h3>
          <ul className="mpp-list">
            <li>Advance notice 30 days before major changes</li>
            <li>Email notifications for important updates</li>
            <li>In-app alerts for visibility</li>
            <li>Archived version history</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📝 Types of Changes</h3>
          <ul className="mpp-list">
            <li>Minor clarifications & typo fixes</li>
            <li>Major data practice adjustments</li>
            <li>Legal or regulatory compliance updates</li>
            <li>Feature-related privacy implications</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">✅ Your Choice</h3>
          <ul className="mpp-list">
            <li>Continue use under current terms during notice period</li>
            <li>Export data before effective date</li>
            <li>Delete account if disagree</li>
            <li>Continued use after date = acceptance</li>
          </ul>
        </div>
        <div className="mpp-inline-note"><strong>Current Version:</strong> Last updated July 17, 2025. The most current version is this page.</div>
      </section>

      <section id="contact" className="mpp-section">
        <h2 className="mpp-section-title">10. Contact Us</h2>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">📞 Privacy Questions & Requests</h3>
          <ul className="mpp-list">
            <li><strong>Email:</strong> <a href="mailto:privacy@docuhounds.com">privacy@docuhounds.com</a> (response within 1 business day)</li>
            <li><strong>In-App Support:</strong> Help section for urgent matters</li>
            <li><strong>General Support:</strong> <a href="mailto:support@docuhounds.com">support@docuhounds.com</a></li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">🔍 When to Contact Us</h3>
          <ul className="mpp-list">
            <li>Data access, correction, deletion</li>
            <li>Privacy & security concerns</li>
            <li>Policy questions & FERPA requests</li>
            <li>Underage account issues</li>
          </ul>
        </div>
        <div className="mpp-subcard"><h3 className="mpp-subcard-title">⏰ Our Response Commitment</h3>
          <ul className="mpp-list">
            <li>Initial privacy response: within 1 business day</li>
            <li>Data requests: typically within 5 days (max 30)</li>
            <li>Security issues: immediate attention</li>
            <li>Account deletion: processed within 5 days</li>
          </ul>
        </div>
      </section>

      <div className="mpp-footer">
        <button type="button" className="mpp-full-link" onClick={scrollToTop}>↑ Back to Top</button>
        <p className="mpp-last-updated">Last updated: July 17, 2025</p>
      </div>
    </div>
  );
}
